import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Briefcase, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtSf } from "../lib/utils";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";

function fmtK(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(2)}K`;
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtMoneyK(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(2)}K`;
  return `$${Number(n).toFixed(0)}`;
}
function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0.00";
  return `$${Number(n).toFixed(2)}`;
}

const ACCENT = "#EC4899";

export default function MoldJobAnalysisCard({ showMexico = false, dept = "MOLD", phase = null, title = "Job Analysis", testId = "mold-job-analysis-card" }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState({ jobs: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (phase) p.phase = phase;
    return p;
  }, [filter, showMexico, phase]);

  const filteredJobs = useMemo(() => {
    const jobs = data.jobs || [];
    if (!search.trim()) return jobs;
    const q = search.toLowerCase();
    return jobs.filter((j) => (j.job || "").toLowerCase().includes(q));
  }, [data.jobs, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/${dept}/job-analysis`, { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, dept]);

  const toggle = (job) => {
    setExpanded((set) => {
      const next = new Set(set);
      next.has(job) ? next.delete(job) : next.add(job);
      return next;
    });
  };

  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden" data-testid={testId}>
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="px-5 py-4 border-b border-border flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Briefcase size={18} style={{ color: ACCENT }} />
            <h3 className="font-heading font-bold text-xl">{title}</h3>
            <span className="text-[11px] text-muted-foreground">
              · {search ? `${filteredJobs.length} / ${data.count ?? 0}` : (data.count ?? 0)} jobs tracked
              {phase && <span className="ml-2 px-1.5 py-0.5 border border-border bg-[#0A0B10] text-[#94A3B8] font-mono">phase = {phase}</span>}
            </span>
          </div>
          <div className="relative w-64 max-w-full" data-testid="job-analysis-search-wrap">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="job-analysis-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search job code…"
              className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#EC4899]/60"
            />
          </div>
        </div>
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="overflow-x-auto" data-testid="job-analysis-table">
        <table className="w-full text-sm text-left">
          <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="py-2.5 pl-4 w-8"></th>
              <th className="py-2.5 px-3">Job Code</th>
              <th className="py-2.5 px-3 text-right">Total SF</th>
              <th className="py-2.5 px-3 text-right">Shipped SF</th>
              <th className="py-2.5 px-3 text-right">Hours</th>
              <th className="py-2.5 px-3 text-right">SF/Hr</th>
              <th className="py-2.5 px-3 text-right">Cost</th>
              <th className="py-2.5 px-3 text-right">Cost/SF</th>
              <th className="py-2.5 px-3 text-right">Scrap SF</th>
              <th className="py-2.5 px-3 text-right">Scrap %</th>
              <th className="py-2.5 px-3 text-right pr-5">Days</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="py-12 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && (data.jobs?.length ?? 0) === 0 && (
              <tr><td colSpan={11} className="py-12 text-center uppercase tracking-widest text-muted-foreground">No jobs in range</td></tr>
            )}
            {!loading && (data.jobs?.length ?? 0) > 0 && filteredJobs.length === 0 && (
              <tr><td colSpan={11} className="py-12 text-center uppercase tracking-widest text-muted-foreground">No jobs match “{search}”</td></tr>
            )}
            {!loading && filteredJobs.map((j) => (
              <RowFragment key={j.job} job={j} expanded={expanded.has(j.job)} toggle={() => toggle(j.job)} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RowFragment({ job, expanded, toggle }) {
  const scrapPct = job.scrapped_pct;
  const scrapColor = (scrapPct ?? 0) > 5 ? "#F87171" : (scrapPct ?? 0) > 0 ? "#FBBF24" : "#94A3B8";
  return (
    <>
      <tr
        data-testid={`job-row-${job.job}`}
        onClick={toggle}
        className={`border-t border-border/40 cursor-pointer transition-colors ${expanded ? "bg-[#1A1C28]" : "hover:bg-[#1A1C28]/40"}`}
      >
        <td className="py-2.5 pl-4">
          {expanded ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
        </td>
        <td className="py-2.5 px-3 font-mono text-white">{job.job}</td>
        <td className="py-2.5 px-3 font-mono text-right">{fmtSf(job.total_sf)}</td>
        <td
          className="py-2.5 px-3 font-mono text-right"
          data-testid={`job-shipped-sf-${job.job}`}
          style={{ color: (job.shipped_sf || 0) > 0 ? "#22D3EE" : "#475569" }}
        >
          {fmtSf(job.shipped_sf)}
        </td>
        <td className="py-2.5 px-3 font-mono text-right">{fmtK(job.hours)}</td>
        <td className="py-2.5 px-3 font-mono text-right" style={{ color: ACCENT }}>{job.sf_per_hour}</td>
        <td className="py-2.5 px-3 font-mono text-right">{fmtMoneyK(job.cost)}</td>
        <td className="py-2.5 px-3 font-mono text-right text-amber-400">{fmtMoney(job.cost_per_sf)}</td>
        <td className="py-2.5 px-3 font-mono text-right" data-testid={`job-scrap-sf-${job.job}`}>
          <span style={{ color: (job.scrapped_sf || 0) > 0 ? "#F87171" : "#94A3B8" }}>{fmtSf(job.scrapped_sf)}</span>
        </td>
        <td className="py-2.5 px-3 font-mono text-right" data-testid={`job-scrap-pct-${job.job}`}>
          <span style={{ color: scrapColor }}>
            {scrapPct === null || scrapPct === undefined ? "—" : `${Number(scrapPct).toFixed(1)}%`}
          </span>
        </td>
        <td className="py-2.5 px-3 font-mono text-right pr-5">{job.days}</td>
      </tr>
      {expanded && (
        <tr className="border-l-2" style={{ borderLeftColor: ACCENT }}>
          <td colSpan={11} className="bg-[#0A0B10]/50 px-5 py-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">Daily Breakdown</div>
            <div className="max-h-[300px] overflow-y-auto border border-border/40">
              <table className="w-full text-xs">
                <thead className="bg-[#1A1C28] sticky top-0 text-[10px] uppercase tracking-widest text-[#94A3B8]">
                  <tr>
                    <th className="py-2 px-3 text-left">Date</th>
                    <th className="py-2 px-3 text-right">SF</th>
                    <th className="py-2 px-3 text-right">Shipped SF</th>
                    <th className="py-2 px-3 text-right">Hours</th>
                    <th className="py-2 px-3 text-right">SF/Hr</th>
                    <th className="py-2 px-3 text-right">Cost</th>
                    <th className="py-2 px-3 text-right">Cost/SF</th>
                  </tr>
                </thead>
                <tbody>
                  {job.daily?.length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-muted-foreground uppercase tracking-widest">No daily activity</td></tr>
                  )}
                  {job.daily?.map((d) => (
                    <tr key={d.date} className="border-t border-border/30">
                      <td className="py-2 px-3 font-mono">{d.date}</td>
                      <td className="py-2 px-3 font-mono text-right">{Number(d.sf).toFixed(2)}</td>
                      <td className="py-2 px-3 font-mono text-right" style={{ color: (d.shipped_sf || 0) > 0 ? "#22D3EE" : "#475569" }}>{Number(d.shipped_sf || 0).toFixed(2)}</td>
                      <td className="py-2 px-3 font-mono text-right">{Number(d.hours).toFixed(2)}</td>
                      <td className="py-2 px-3 font-mono text-right" style={{ color: ACCENT }}>{Number(d.sf_per_hour).toFixed(2)}</td>
                      <td className="py-2 px-3 font-mono text-right">{fmtMoney(d.cost)}</td>
                      <td className="py-2 px-3 font-mono text-right text-amber-400">{fmtMoney(d.cost_per_sf)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
